import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import Big from 'big.js'
import {format} from 'date-fns'

import FlightArrowSvg from '@/assets/flight/icons/flight-arrow.svg?react'
import successSvg from '@/assets/icons/success.svg'
import spinnerLottie from '@/assets/lotties/spinner.lottie'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import type {Booking} from '@/lib/flight/flightModel'
import {confirmBooking, expireBooking, getBooking} from '@/lib/flight/flightService'
import HeadTags from '@/lib/head/HeadTags'
import useCashback from '@/lib/payment/hooks/useCashback'
import {FlightCheckoutRoute, FlightDetailsRoute, FlightRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {NO_REFETCH_OPTIONS} from '@/utils/query/query'

import style from './Checkout.module.scss'

function useBookingQuery(bookingId: string | undefined) {
  return useQuery({
    queryKey: ['booking', bookingId],
    queryFn: bookingId ? async () => getBooking({bookingId}) : skipToken,
    ...NO_REFETCH_OPTIONS,
  })
}

function Result({booking}: {booking: Booking}) {
  const navigate = useNavigate()

  const onDone = useCallback(() => {
    void navigate({
      to: FlightRoute.fullPath,
    })
  }, [navigate])

  const price = Big(booking.price)
  const discountValue = Big(-1).times(Big(booking.moreInfo.amountDiscount))
  const roundUp = Big(booking.moreInfo.amountRoundUp)
  const total = price.add(discountValue).add(roundUp)

  const numOfAdult = booking.passengers.filter(pax => pax.type === 'adult').length
  const numOfChild = booking.passengers.filter(pax => pax.type === 'child').length
  const numOfInfantInSeat = booking.passengers.filter(pax => pax.type === 'infant_in_seat').length
  const numOfInfantOnLap = booking.passengers.filter(pax => pax.type === 'infant_on_lap').length
  const passengerText = (() => {
    const texts = []
    if (numOfAdult > 0) texts.push(`${numOfAdult} adult${numOfAdult > 1 ? 's' : ''}`)
    if (numOfChild > 0) texts.push(`${numOfChild} child${numOfChild > 1 ? 'ren' : ''}`)
    if (numOfInfantInSeat > 0)
      texts.push(`${numOfInfantInSeat} infant${numOfInfantInSeat > 1 ? 's' : ''} in seat`)
    if (numOfInfantOnLap > 0)
      texts.push(`${numOfInfantOnLap} infant${numOfInfantOnLap > 1 ? 's' : ''} on lap`)
    return texts.join(', ')
  })()

  const {data: cashback} = useCashback(total)

  return (
    <div className={style.Result}>
      <div role='alert'>
        <img src={successSvg} alt='' className={style.ResultDecoration} />
        <h1 className={style.Heading}>
          <span role='alert'>Successfully purchased</span>
        </h1>
        <div className={style.Description}>
          We have sent an email to <span className={style.Email}>{booking.contactInfo.email}</span>{' '}
          with everything you need to know about your order
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <h2>Booking details</h2>
          <div className={style.OrderId}>
            Booking number <span>{booking.id}</span>
          </div>
        </div>
        <div>
          <VisuallyHidden as='h3'>Contact info:</VisuallyHidden>
          {`${booking.contactInfo.firstName} ${booking.contactInfo.lastName}`} ({passengerText})
        </div>
        <div className={style.FlightSegments}>
          {booking.flightSegments.map((flightSegment, idx) => {
            const firstFlight = flightSegment.flights[0]
            const lastFlight = flightSegment.flights[flightSegment.flights.length - 1]

            if (!firstFlight || !lastFlight) return null
            const durationText = `${Math.floor(flightSegment.total_duration / 60)}h${flightSegment.total_duration % 60}m`

            const numOfStops = flightSegment.flights.length - 1

            return (
              <div
                key={idx}
                className={style.FlightSegment}
                role='region'
                aria-label={`Flight ${idx + 1}`}
              >
                <VisuallyHidden as='h3'>
                  Flight {idx + 1}: From {firstFlight.departure_airport.id} to{' '}
                  {lastFlight.arrival_airport.id}
                </VisuallyHidden>
                <div className={style.FlightSegmentHeader}>
                  <img src={firstFlight.airline_logo} alt='' loading='lazy' />
                  <div className={style.FlightSegmentHeaderDetails}>
                    <div className={style.FlightSegmentHeaderDetailsTitle}>
                      <VisuallyHidden as='h4'>Airline:</VisuallyHidden>
                      {firstFlight.airline} - <VisuallyHidden as='h4'>Travel class:</VisuallyHidden>
                      {firstFlight.travel_class}
                    </div>
                    <div className={style.FlightSegmentHeaderDetailsSubtitle}>
                      <VisuallyHidden as='h4'>Flight number:</VisuallyHidden>
                      {firstFlight.flight_number}
                    </div>
                  </div>
                </div>
                <div className={style.FlightSegmentBody}>
                  <div className={style.FlightSegmentBodyDestination}>
                    <div className={style.FlightSegmentBodyDestinationCity}>
                      <VisuallyHidden as='h4'>Departure airport:</VisuallyHidden>
                      {firstFlight.departure_airport.id}
                    </div>
                    <div className={style.FlightSegmentBodyDestinationTime}>
                      <VisuallyHidden as='h4'>Departure time:</VisuallyHidden>
                      {format(new Date(firstFlight.departure_airport.time), 'hh:mm a')}
                    </div>
                    <div className={style.FlightSegmentBodyDestinationDate}>
                      <VisuallyHidden as='h4'>Departure date:</VisuallyHidden>
                      {format(new Date(firstFlight.departure_airport.time), 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <div className={style.FlightSegmentBodyDuration}>
                    <div className={style.FlightSegmentBodyDurationText}>
                      <VisuallyHidden as='h4'>Duration:</VisuallyHidden>
                      {durationText} (
                      {numOfStops === 0
                        ? 'Non-stop'
                        : `${numOfStops} stop${numOfStops > 1 ? 's' : ''}`}
                      )
                    </div>
                    <FlightArrowSvg />
                  </div>
                  <div className={style.FlightSegmentBodyDestination}>
                    <div className={style.FlightSegmentBodyDestinationCity}>
                      <VisuallyHidden as='h4'>Arrival airport:</VisuallyHidden>
                      {lastFlight.arrival_airport.id}
                    </div>
                    <div className={style.FlightSegmentBodyDestinationTime}>
                      <VisuallyHidden as='h4'>Arrival time:</VisuallyHidden>
                      {format(new Date(lastFlight.arrival_airport.time), 'hh:mm a')}
                    </div>
                    <div className={style.FlightSegmentBodyDestinationDate}>
                      <VisuallyHidden as='h4'>Arrival date:</VisuallyHidden>
                      {format(new Date(lastFlight.arrival_airport.time), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.DeliveryInfo}>
        <div className={style.DeliveryItems}>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Subtotal ({booking.passengers.length} passengers)
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={price}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          {roundUp.gt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>Donate change to NFB</div>
              <div className={style.DeliveryItemPrice}>
                <Price
                  price={roundUp}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          )}
          {discountValue.lt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>Discount (total)</div>
              <div className={clsx(style.DeliveryItemPrice, style.Discount)}>
                <Price
                  price={discountValue}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.Total}>
        Total
        <span>
          <Price
            price={total}
            options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
          />
        </span>
      </div>
      {cashback && (
        <div className={clsx(style.DeliveryInfo, style.Rewards)}>
          <div className={style.DeliveryItems}>
            <div className={style.DeliveryItem}>
              <div className={clsx(style.DeliveryItemName, style.Good)}>
                Rewards ({cashback.rate.mul(100).toNumber()}%)<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={clsx(style.DeliveryItemPrice, style.Discount)}>
                <Price
                  price={cashback.value}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <PrimaryButton className={style.DoneBtn} onPress={onDone}>
        Done
      </PrimaryButton>
    </div>
  )
}

export default memo(function Checkout() {
  const {bookingId, failed, flightId} = FlightCheckoutRoute.useSearch()
  const navigate = useNavigate()

  const {data: booking, isLoading} = useBookingQuery(bookingId)

  ;(function expireOrConfirmBooking() {
    if (!bookingId) return
    if (failed) {
      void expireBooking({bookingId}).catch(() => {
        /* do nothing */
      })
    } else {
      void confirmBooking({bookingId}).catch(() => {
        /* do nothing */
      })
    }
  })()
  ;(function navigateToFlightDetailsIfTheBookingIsFailed() {
    if (!booking) return
    if (!failed) return

    void navigate({
      to: FlightDetailsRoute.fullPath,
      params: {
        flightId: flightId ?? booking.flightId ?? '',
      },
    })
  })()

  console.log(booking)

  if (failed) return null

  return (
    <>
      <HeadTags title={'Checkout successfully'} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar transparentBg noLogo haveCart={false} variant='light' />
        <main className={style.CheckoutPage} {...skipTargetProps('main-content')}>
          <div className={style.CheckoutContent}>
            {isLoading && (
              <div className={style.SpinnerContainer}>
                <DotLottieReact
                  src={spinnerLottie}
                  autoplay
                  loop
                  className={style.Spinner}
                  aria-label='Loading product...'
                  role='progressbar'
                />
              </div>
            )}
            {!isLoading && booking && typeof booking !== 'string' && <Result booking={booking} />}
          </div>
        </main>
        <Footer />
      </div>
    </>
  )
})
